import { IGoogleTagManagerEvent } from '../store';
import IBooking from '../interfaces/IBooking';
import { ICharge } from '../interfaces/ICharge';
import ISession from '../interfaces/ISession';
import PackageTierPrice from '../interfaces/IPackageTierPrice';
import SessionTime from '../interfaces/ISessionTime';
import Site from '../interfaces/ISite';
import Experience from '../interfaces/IExperience';
import TagManager from 'react-gtm-module';
import { IDiscountCode } from '../interfaces/IDiscountCode';
import { IAttributionState } from '../store/Attribution';
import regionConverter from '../helpers/regionConverter';
import { IGiftVoucher } from '../interfaces/GiftVouchers/IGiftVoucher';
import { FeatureFlagState } from "../store/featureFlags";
import { ICurrency } from '../interfaces/ICurrency';

class GoogleTagManager {
    public static init(googleTagManagerKey: string, dataLayer: string = 'dataLayer') {
        //Set up the new gtm scripts using the tagManager plugin that doesn't clean up after itself
        //allowing the tags to pile up on the page does not cause any issues
        const tagManagerArgs = {
            gtmId: googleTagManagerKey,
            dataLayerName: dataLayer,
        };
        console.log(
            "Initialize gtm for container Key: '" + googleTagManagerKey + "', and data layer: '" + dataLayer + "'"
        );
        TagManager.initialize(tagManagerArgs);
    }

    public static sendToDataLayer(dataLayer: any, siteDataLayer?: string) {
        const baseDataLayerArgs = {
            dataLayer: dataLayer,
            dataLayerName: 'dataLayer',
        };
        TagManager.dataLayer(baseDataLayerArgs);

        if (siteDataLayer) {
            const siteDataLayerArgs = {
                dataLayer: dataLayer,
                dataLayerName: siteDataLayer,
            };
            TagManager.dataLayer(siteDataLayerArgs);
        }
    }

    public static sendToSiteDataLayer(dataLayer: any, siteDataLayer: string) {
        const siteDataLayerArgs = {
            dataLayer: dataLayer,
            dataLayerName: siteDataLayer,
        };
        TagManager.dataLayer(siteDataLayerArgs);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static addAttributionToDataLayerEvent(dataLayer: any, attribution: IAttributionState): any {
        // Values originate from URLSearchParams.get() in App.tsx, which already URL-decodes them.
        // Decoding again throws URIError on any value containing a literal '%' (e.g. campaign
        // params with %25), which crashes onLocationChanged and blanks the SPA.
        dataLayer.attribution = {
            utm_campaign: attribution.utm_campaign,
            utm_content: attribution.utm_content,
            utm_id: attribution.utm_Id,
            utm_medium: attribution.utm_medium,
            utm_source: attribution.utm_source,
            utm_term: attribution.utm_term,

            internal_source: attribution.internal_source,
            internal_sourcePage: attribution.internal_sourcePage,
            internal_componentType: attribution.internal_componentType,
            internal_componentInternalName: attribution.internal_componentInternalName,
            internal_componentTitle: attribution.internal_componentTitle,
            internal_buttonText: attribution.internal_buttonText,
        };

        return dataLayer;
    }

    public static addFeatureFlagsToDataLayerEvent(dataLayer: any, featureFlags: FeatureFlagState): any {

        const state = featureFlags?.flags ?? {};

        //grab the label for each enabled key
        const enabledKeys: string[] = Object.keys(state).filter(k => state[k]?.enabled);

        //spread the currently active feature flag state into the gtm event
        dataLayer.featureFlags = {
            flags: { ...state },
            active: enabledKeys
        };
        
        return dataLayer;
    }

    public static pageView(pathname: string, title: string, attribution: IAttributionState, featureFlags: FeatureFlagState, siteDataLayer?: string) {
        let dataLayer = {
            category: 'Booking Engine',
            event: 'pageview',
            page: {
                url: pathname,
                title: title,
            },
            eventProps: {
                value: pathname,
                title: title,
            },
        };

        dataLayer = GoogleTagManager.addAttributionToDataLayerEvent(dataLayer, attribution);
        dataLayer = GoogleTagManager.addFeatureFlagsToDataLayerEvent(dataLayer, featureFlags);

        GoogleTagManager.sendToDataLayer(dataLayer, siteDataLayer);
    }

    public static modalView(_pathname: string, _trackers?: string[]) {
        // if (trackers) ReactGA.modalview(pathname, trackers);
        // else ReactGA.modalview(pathname);
    }

    public static sendEnhancedDataLayerEvent(
        request: IGoogleTagManagerEvent,
        purchaseState: object,
        cart: object | undefined,
        attribution: IAttributionState,
        featureFlags: FeatureFlagState,
        siteDataLayer?: string
    ) {
        const dataLayerReset = {
            event: undefined,
            eventProps: undefined,
        };

        let enhancedDataLayer = {
            category: 'Booking Engine',
            event: request.event,
            eventProps: request.eventProps,
            state: purchaseState,
            cart: cart,
        };

        enhancedDataLayer = GoogleTagManager.addAttributionToDataLayerEvent(enhancedDataLayer, attribution);
        enhancedDataLayer = GoogleTagManager.addFeatureFlagsToDataLayerEvent(enhancedDataLayer, featureFlags);

        GoogleTagManager.sendToDataLayer(enhancedDataLayer, siteDataLayer);
        GoogleTagManager.sendToDataLayer(dataLayerReset, siteDataLayer);
    }

    public static event(
        request: Array<IGoogleTagManagerEvent> | IGoogleTagManagerEvent,
        purchaseState: object,
        cart: object | undefined,
        attribution: IAttributionState,
        featureFlags: FeatureFlagState,
        siteDataLayer?: string
    ) {
        if (Array.isArray(request)) {
            request.forEach((event) => {
                GoogleTagManager.sendEnhancedDataLayerEvent(event, purchaseState, cart, attribution, featureFlags, siteDataLayer);
            });
        } else {
            GoogleTagManager.sendEnhancedDataLayerEvent(request, purchaseState, cart, attribution, featureFlags, siteDataLayer);
        }
    }

}

export class GoogleTagManagerEvents {
    public static PurchaseTypeSelected(purchaseType: string): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'purchase type selected',
                label: 'purchase type',
                event: 'Selected Purchase Type',
                eventProps: {
                    type: 'Button',
                    value: purchaseType,
                },
            },
        ];
    }

    // Bookings
    public static ReserveBooking(charge: ICharge): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'proceed to checkout',
                label: 'booking',
                value: charge.total,
                event: 'Proceed to Checkout',
                eventProps: {
                    type: 'Button',
                    value: charge.total,
                },
            },
        ];
    }

    public static ConfirmBooking(
        _charge: ICharge,
        _session: ISession,
        booking: IBooking,
        _siteCode: string,
        _appliedCodes: Array<IDiscountCode>,
        _gtmKey?: string
    ): Array<IGoogleTagManagerEvent> {

        const transactionId = `${booking.bookingId}`;

        return [
            {
                category: '',
                action: '',
                label: '',
                event: 'Checkout Complete',
                eventProps: {
                    type: 'Browser',
                    value: transactionId,
                },
            },
        ];
    }

    public static ConfirmInvoice(
        invoiceId: number,
    ): Array<IGoogleTagManagerEvent> {

        const transactionId = `${invoiceId}`;

        return [
            {
                category: '',
                action: '',
                label: '',
                event: 'Checkout Complete',
                eventProps: {
                    type: 'Browser',
                    value: transactionId,
                },
            },
        ];
    }

    public static BookingExpired(bookingRef: string, bookingId: number): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'booking expired',
                label: bookingRef,
                value: bookingId,
                event: 'Booking Expired',
                eventProps: {
                    type: 'Browser',
                    value: bookingId,
                },
            },
        ];
    }

    public static RecordEmailAddress(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'contact details',
                label: 'input email address',
                event: 'Input Email Address',
                eventProps: {
                    type: 'Input',
                },
            },
        ];
    }

    public static RecordFullName(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'contact details',
                label: 'input full name',
                event: 'Input Full Name',
                eventProps: {
                    type: 'Input',
                },
            },
        ];
    }

    public static RecordPhoneNumber(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'contact details',
                label: 'input phone number',
                event: 'Input Phone Number',
                eventProps: {
                    type: 'Input',
                },
            },
        ];
    }

    public static AppliedDiscountCode(discountCode: string): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'applied discount code',
                label: discountCode,
                event: 'Applied Discount Code',
                eventProps: {
                    type: 'Input',
                    value: discountCode,
                },
            },
        ];
    }

    public static RemovedDiscountCode(discountCode: string): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'removed discount code',
                label: discountCode,
                event: 'Removed Discount Code',
                eventProps: {
                    type: 'Input',
                    value: discountCode,
                },
            },
        ];
    }

    public static InvalidDiscountCode(discountCode: string): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'invalid discount code',
                label: discountCode,
                event: 'Invalid Discount Code',
                eventProps: {
                    type: 'Input',
                    value: discountCode,
                },
            },
        ];
    }

    public static PlayerSearchReceived(playerFound: boolean): Array<IGoogleTagManagerEvent> {
        if (playerFound)
            return [
                {
                    category: 'checkout',
                    action: 'player search',
                    label: 'returning player',
                    event: 'Player Search',
                    eventProps: {
                        type: 'Browser',
                        value: playerFound,
                    },
                },
            ];
        else return [];
    }

    public static PlayersSelected(players: number): Array<IGoogleTagManagerEvent> {

        return [
            {
                category: 'tickets',
                action: 'selected players',
                label: 'players',
                value: players,
                event: 'Select Players',
                eventProps: {
                    type: 'Button',
                    value: players,
                },
            },
        ];
    }

    public static PrivateEventTypeSelected(privateEventType: number): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'selected private event type',
                label: 'private event type',
                value: privateEventType,
                event: 'Select Private Event Type',
                eventProps: {
                    type: 'Button',
                    value: privateEventType,
                },
            },
        ];
    }

    public static PrivateUpgradeSelected(upgradeChecked: boolean): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'selected private upgrade',
                label: 'private upgrade',
                value: upgradeChecked ? 1 : 0,
                event: 'Select Private Upgrade',
                eventProps: {
                    type: 'Button',
                    value: upgradeChecked,
                },
            },
        ];
    }

    public static SessionTimeSelected(time: SessionTime): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'selected time',
                label: time.time,
                event: 'Select Time',
                eventProps: {
                    type: 'Button',
                    value: time.time,
                    slots_remaining: time.slotsRemaining,
                },
            },
        ];
    }

    public static SessionDateSelected(date: string): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'selected date',
                label: date,
                event: 'Select Date',
                eventProps: {
                    type: 'Button',
                    value: date,
                },
            },
        ];
    }

    public static SessionPackageSelected(session: ISession): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'selected session',
                label: session.package.alias,
                value: session.package.packageId,

                event: 'Package Added To Cart',
                eventProps: {
                    type: 'Button',
                    session_time: session.startTime,
                    package: session.package.header,
                    package_id: session.package.packageId,
                    package_name: session.package.alias,
                },
            },
        ];
    }

    public static LocationRequested(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'location requested',
                label: 'browser',
                event: 'Location Requested',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    public static AggressiveLocationPromptTriggered(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'aggressive location prompt triggered',
                label: 'location widget',
                event: 'aggressive location prompt triggered',
                eventProps: {
                    type: 'browser',
                },
            },
        ];
    }

    public static ShareLocationClicked(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'share location clicked',
                label: 'location widget',
                event: 'share location clicked',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }
    public static NearbyVenueClicked(site: Site): Array<IGoogleTagManagerEvent> {
        const region = regionConverter.regionIdToString(site.regionId);

        return [
            {
                category: 'tickets',
                action: 'select venue',
                label: 'location widget',
                event: 'Select Venue',
                eventProps: {
                    type: 'location widget',
                    value: site.name,
                    venue_id: site.siteId,
                    venue_code: site.code,
                    country: site.country,
                    region: region,
                },
            },
        ];
    }

    public static ChangeVenueClicked(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'change venue clicked',
                label: 'location widget',
                event: 'change venue clicked',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    public static LocationWidget_backClicked(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'location widget clicked back',
                label: '',
                event: 'Clicked back',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    public static LocationWidget_toggleVenueDetails(_isOpen:boolean): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'fucking what',
                label: '',
                event: 'location widget details toggled',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    public static LocationWidgetClick_region(clickValue: string, clickKey: string): Array<IGoogleTagManagerEvent> {
        return GoogleTagManagerEvents.LocationWidgetClick("region", clickValue, clickKey);
    }
    public static LocationWidgetClick_country(clickValue: string, clickKey: string): Array<IGoogleTagManagerEvent> {
        return GoogleTagManagerEvents.LocationWidgetClick("country", clickValue, clickKey);
    }
    public static LocationWidgetClick_state(clickValue: string, clickKey: string): Array<IGoogleTagManagerEvent> {
        return GoogleTagManagerEvents.LocationWidgetClick("state", clickValue, clickKey);
    }
    public static LocationWidgetClick(clickType:string, clickValue:string, clickKey: string): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: `location widget click ${clickType}`,
                label: clickValue,
                event: 'location widget click',
                eventProps: {
                    type: 'Button',
                    value: clickValue,
                    clickType: clickType,
                    key: clickKey
                },
            },
        ];
    }


    public static SiteSelected(site: Site, nearest: boolean, nearby: boolean = false, locationWidget: boolean = false): Array<IGoogleTagManagerEvent> {

        const region = regionConverter.regionIdToString(site.regionId);
        const selectionModeLabel = nearby ? 'locationWidgetNearby'
            : locationWidget ? 'locationWidget'
            : nearest ? 'browser' //backwards compatible
                    : 'dropdown'; //backwards compatible

        const selectionModeType = nearby ? 'Location Widget Nearby'
            : locationWidget ? 'Location Widget'
                : nearest ? 'Browser' //backwards compatible
                    : 'Dropdown'; //backwards compatible

        return [
            {
                category: 'tickets',
                action: 'select venue',
                label: selectionModeLabel,
                event: 'Select Venue',
                eventProps: {
                    type: selectionModeType,
                    value: site.name,
                    venue_id: site.siteId,
                    venue_code: site.code,
                    nearest: nearest,
                    nearby: nearby,
                    country: site.country,
                    region: region,
                },
            },
        ];
    }

    public static GameSelected(game: Experience | undefined): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'select game',
                label: game?.name ?? 'All Games',
                event: 'Select Game',
                eventProps: {
                    type: 'Dropdown',
                    value: game?.name,
                    alias: game?.alias,
                },
            },
        ];
    }

    public static ViewedGroupEnquiry(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'viewed group enquiry',
                label: '',
                event: 'Group Enquiry',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }
    public static SentGroupEventEnquiry(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'sent group enquiry',
                label: '',
                event: 'Group Enquiry',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    public static ViewedPackageDetails(
        session: ISession,
        siteCode: string,
        ticketCount: number,
        appliedCodes: Array<IDiscountCode>
    ): Array<IGoogleTagManagerEvent> {

        const discount = appliedCodes[0] != null ? (appliedCodes[0]?.amount * 100) / ticketCount / 100 : 0;

        const priceTierTypeId = session?.priceTierTypeId;
        const packageTierPrice = session?.package.packageTierPrices.find(
            (ptp: PackageTierPrice) => ptp.priceTierType.priceTierTypeId === priceTierTypeId
        );

        const pricing = packageTierPrice?.price;
        const cost = packageTierPrice?.price.cost;
        const currencyCode = pricing?.currencyCode ?? ('' as string);

        return [
            {
                category: 'tickets',
                action: 'viewed package details',
                label: session.package.alias,
                value: session.packageId,
                event: 'Package Details',
                eventProps: {
                    type: 'Button',
                    session_time: session.startTime,
                    package: session.package.header,
                    package_name: session.package.alias,
                    items: [
                        {
                            item_id: session.package.header,
                            item_name: session.package.alias,
                            affiliation: siteCode,
                            currency: currencyCode,
                            discount: discount,
                            item_brand: 'Zero Latency',
                            item_category: 'Tickets',
                            quantity: ticketCount,
                            price: cost,
                        },
                    ],
                },
            },
        ];
    }

    public static ViewedPackageVideo(
        session: ISession,
        siteCode: string,
        ticketCount: number,
        appliedCodes: Array<IDiscountCode>
    ): Array<IGoogleTagManagerEvent> {

        const discount = appliedCodes[0] != null ? (appliedCodes[0]?.amount * 100) / ticketCount / 100 : 0;

        const priceTierTypeId = session?.priceTierTypeId;
        const packageTierPrice = session?.package.packageTierPrices.find(
            (ptp: PackageTierPrice) => ptp.priceTierType.priceTierTypeId === priceTierTypeId
        );

        const pricing = packageTierPrice?.price;
        const cost = packageTierPrice?.price.cost;
        const currencyCode = pricing?.currencyCode ?? ('' as string);

        return [
            {
                category: 'tickets',
                action: 'viewed package video',
                label: session.package.alias,
                value: session.packageId,
                event: 'Package Video',
                eventProps: {
                    type: 'Video',
                    session_time: session.startTime,
                    package: session.package.header,
                    package_name: session.package.alias,
                    items: [
                        {
                            item_id: session.package.header,
                            item_name: session.package.alias,
                            affiliation: siteCode,
                            currency: currencyCode,
                            discount: discount,
                            item_brand: 'Zero Latency',
                            item_category: 'Tickets',
                            quantity: ticketCount,
                            price: cost,
                        },
                    ],
                },
            },
        ];
    }

    public static ClickedOutboundLink(outboundUrl: string): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'clicked outbound link',
                label: outboundUrl,
                event: 'Outbound Link',
                eventProps: {
                    type: 'Button',
                    value: outboundUrl,
                },
            },
        ];
    }

    public static ClickedReadFAQ(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'read FAQ',
                label: '',
                event: 'FAQ',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    public static ClickedReadTOS(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'read terms of service',
                label: '',
                event: 'Terms of Service',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    public static ClickedReadPrivacyPolicy(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'read privacy policy',
                label: '',
                event: 'Privacy Policy',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    public static ModifiedBooking(booking: IBooking): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'modified booking',
                label: 'booking id',
                value: booking.bookingId,
                event: 'Modified Booking',
                eventProps: {
                    type: 'Button',
                    value: booking.bookingId,
                },
            },
        ];
    }

    public static ValidPaymentDetails(gateway: string): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'valid payment details',
                label: gateway,
                event: 'Valid Payment Details',
                eventProps: {
                    type: 'Input',
                    value: gateway,
                },
            },
        ];
    }

    public static PaymentFailed(gateway: string): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'payment failed',
                label: gateway,
                event: 'Payment Failed',
                eventProps: {
                    type: 'Input',
                    value: gateway,
                },
            },
        ];
    }

    public static CopiedBookingShareLink(booking: IBooking): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'complete',
                action: 'share details',
                label: 'booking id',
                value: booking.bookingId,
                event: 'Share Details',
                eventProps: {
                    type: 'Button',
                    value: booking.bookingId,
                },
            },
        ];
    }

    public static ClosedSurvey(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'complete',
                action: 'closed survey',
                label: '',
                event: 'Closed Survey',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    public static SubscribedEmailToMarketing(isChecked: boolean): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'marketing email subscribe',
                label: '',
                event: 'Marketing Email Subscribe',
                eventProps: {
                    value: isChecked,
                },
            },
        ];
    }

    public static SubscribedPhoneToMarketing(isChecked: boolean): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'marketing phone subscribe',
                label: '',
                event: 'Marketing Phone Subscribe',
                eventProps: {
                    value: isChecked,
                },
            },
        ];
    }

    // Gift Vouchers

    public static CustomGiftVoucherAmountInput(amount: number, isGlobal: boolean): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'gift voucher amount input',
                label: 'gift voucher',
                event: 'Custom Gift Voucher Amount input',
                eventProps: {
                    type: 'Input',
                    value: amount,
                    isGlobal: isGlobal,
                },
            },
        ];
    }

    public static FixedGiftVoucherAmountSelected(amount: number, isGlobal: boolean): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'gift voucher amount selected',
                label: 'gift voucher',
                event: 'Fixed Gift Voucher Amount Selected',
                eventProps: {
                    type: 'Button',
                    value: amount,
                    isGlobal: isGlobal,
                },
            },
        ];
    }

    public static GiftVoucherRecipientSelected(toRecipient: boolean, isGlobal: boolean): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'gift voucher recipient selected',
                label: 'gift voucher',
                event: 'Gift Voucher Recipient Selected',
                eventProps: {
                    type: 'Button',
                    toRecipient: toRecipient,
                    isGlobal: isGlobal,
                },
            },
        ];
    }

    public static RecordRecipientEmailAddress(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'recipient contact details',
                label: 'input recipient email address',
                event: 'Input Recipient Email Address',
                eventProps: {
                    type: 'Input',
                },
            },
        ];
    }

    public static RecordRecipientFullName(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'recipient contact details',
                label: 'input recipient full name',
                event: 'Input Recipient Full Name',
                eventProps: {
                    type: 'Input',
                },
            },
        ];
    }

    public static RecordRecipientMessage(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'recipient contact details',
                label: 'input recipient message',
                event: 'Input Recipient Message',
                eventProps: {
                    type: 'Input',
                },
            },
        ];
    }

    public static ReserveGiftVoucher(charge: ICharge, isGlobal: boolean): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'proceed to checkout',
                label: 'gift voucher',
                value: charge.total,
                event: 'Proceed to Checkout',
                eventProps: {
                    type: 'Button',
                    value: charge.total,
                    isGlobal: isGlobal,
                },
            },
        ];
    }

    public static ConfirmGiftVoucher(
        _charge: ICharge,
        giftVoucher: IGiftVoucher,
        _siteCode: string,
        isGlobal: boolean,
        _gtmKey?: string,
    ): Array<IGoogleTagManagerEvent> {
        const transactionId = `${giftVoucher.giftVoucherId}`;

        return [
            {
                category: '',
                action: '',
                label: '',
                event: 'Checkout Complete',
                eventProps: {
                    type: 'Browser',
                    value: transactionId,
                    isGlobal: isGlobal,
                },
            },
        ];
    }

    public static ModifiedGiftVoucher(giftVoucher: IGiftVoucher): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'checkout',
                action: 'modified gift voucher',
                label: 'gift voucher id',
                value: giftVoucher.giftVoucherId,
                event: 'Modified Gift Voucher',
                eventProps: {
                    type: 'Button',
                    value: giftVoucher.giftVoucherId,
                },
            },
        ];
    }

    public static RecordPriceSet(venue: string, priceCode: string, source: string): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tickets',
                action: 'price code allocated',
                label: 'price code',
                event: 'Price Code Allocated',
                eventProps: {
                    type: 'Price Code',
                    venue: venue,
                    priceCode: priceCode,
                    source: source,
                },
            },
        ];
    }

    public static GlobalGifts_TermsAndConditionsLinkClicked(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'global-gift',
                action: 'read terms and conditions',
                label: '',
                event: 'Terms and conditions',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    public static GlobalGifts_BlackFridayTermsAndConditionsLinkClicked(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'global-gift',
                action: 'black friday read terms and conditions',
                label: '',
                event: 'Black Friday Terms and conditions',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    public static GlobalGifts_CurrencySelected(currency: ICurrency): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'global-gift',
                action: 'curency selected',
                label: 'curency id',
                value: currency.id,
                event: 'Curency Selected',
                eventProps: {
                    currencyCode: currency.code,
                    currencyName: currency.name
                },
            },
        ];
    }

    // --- Player Count Upsell ---

    public static UpsellShown(variant: string, currentPlayers: number, minimumPlayers: number): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'upsell',
                action: 'PlayerCountUpsell_Shown',
                label: 'player count upsell',
                event: 'PlayerCountUpsell_Shown',
                eventProps: {
                    type: 'Browser',
                    variant: variant,
                    currentPlayers: currentPlayers,
                    minimumPlayers: minimumPlayers,
                },
            },
        ];
    }

    public static UpsellPlayersUpdated(previousPlayers: number, newPlayers: number, minimumPlayers: number, offerMet: boolean): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'upsell',
                action: 'PlayerCountUpsell_PlayersUpdated',
                label: 'player count upsell',
                value: newPlayers,
                event: 'PlayerCountUpsell_PlayersUpdated',
                eventProps: {
                    type: 'Button',
                    previousPlayers: previousPlayers,
                    newPlayers: newPlayers,
                    minimumPlayers: minimumPlayers,
                    offerMet: offerMet,
                },
            },
        ];
    }

    public static UpsellOfferAccepted(previousPlayers: number, newPlayers: number, minimumPlayers: number): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'upsell',
                action: 'PlayerCountUpsell_OfferAccepted',
                label: 'player count upsell',
                value: newPlayers,
                event: 'PlayerCountUpsell_OfferAccepted',
                eventProps: {
                    type: 'Button',
                    previousPlayers: previousPlayers,
                    newPlayers: newPlayers,
                    minimumPlayers: minimumPlayers,
                    playerDelta: newPlayers - previousPlayers,
                },
            },
        ];
    }

    public static UpsellCardExpanded(): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'upsell',
                action: 'PlayerCountUpsell_CardExpanded',
                label: 'player count upsell',
                event: 'PlayerCountUpsell_CardExpanded',
                eventProps: {
                    type: 'Button',
                },
            },
        ];
    }

    // --- Tiered Group Size Discount ---

    public static TieredGroupSizeDiscountShown(variant: string, currentPlayers: number): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tieredGroupSizeDiscount',
                action: 'TieredGroupSizeDiscount_Shown',
                label: 'tiered group size discount',
                event: 'TieredGroupSizeDiscount_Shown',
                eventProps: {
                    type: 'Browser',
                    variant: variant,
                    currentPlayers: currentPlayers,
                },
            },
        ];
    }

    public static TieredGroupSizeDiscountPlayersUpdated(previousPlayers: number, newPlayers: number): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tieredGroupSizeDiscount',
                action: 'TieredGroupSizeDiscount_PlayersUpdated',
                label: 'tiered group size discount',
                value: newPlayers,
                event: 'TieredGroupSizeDiscount_PlayersUpdated',
                eventProps: {
                    type: 'Button',
                    previousPlayers: previousPlayers,
                    newPlayers: newPlayers,
                },
            },
        ];
    }

    public static TieredGroupSizeDiscountOfferAccepted(previousPlayers: number, newPlayers: number): Array<IGoogleTagManagerEvent> {
        return [
            {
                category: 'tieredGroupSizeDiscount',
                action: 'TieredGroupSizeDiscount_OfferAccepted',
                label: 'tiered group size discount',
                value: newPlayers,
                event: 'TieredGroupSizeDiscount_OfferAccepted',
                eventProps: {
                    type: 'Button',
                    previousPlayers: previousPlayers,
                    newPlayers: newPlayers,
                    playerDelta: newPlayers - previousPlayers,
                },
            },
        ];
    }
}

export default GoogleTagManager;
